import { AgError } from '@australiangreens/ag-error';

const LIST_719_REGEX = /.*api_key.*/i;

type ErrorDetails = {
  _likelyClass: string;
  name: string;
  message: string;
  [key: string]: string;
};

/**
 * Extract as much information from an error-like thing as possible based on a
 * few assumptions.
 */
export function extractErrorDetails(err: unknown, includeStack = false) {
  const details: ErrorDetails = {
    _likelyClass: 'unknown',
    name: 'unknown',
    message: '-',
  };

  if (isNonNullObject(err)) {
    // Some things are not enumerable, such as hierarchicalName in AgError and
    // (I think) name, message in Error

    if (err instanceof Error) {
      details._likelyClass = looksLikeOAuthError(err) ? 'OAuthError' : 'Error';
      details.name = err.name;
      if (!LIST_719_REGEX.test(err.message)) details.message = err.message;
    }

    if (err instanceof AgError) {
      details._likelyClass = 'AgError';
      details.hierarchicalName = err.hierarchicalName;
    }

    // Get now all remaining enumerable props, only using if not already
    // explicitly set
    for (const [key, val] of Object.entries(err)) {
      if (key in details || (key === 'stack' && !includeStack)) continue;
      // This is kind of arbitrary, the key thing is we always want strings of
      // some kind.
      if (val === undefined) {
        details[key] = 'undefined';
      } else if (val === null) {
        details[key] = 'null';
      } else if (isNonNullObject(val) || Array.isArray(val)) {
        details[key] = JSON.stringify(val);
      } else {
        details[key] = val.toString();
      }
      if (LIST_719_REGEX.test(details[key])) details[key] = '-';
    }

    // If we want the stack, add it last
    if (includeStack && 'stack' in err && typeof err.stack === 'string') details.stack = err.stack;
  } else if (err === null) {
    details._likelyClass = 'null';
  } else if (err === undefined) {
    details._likelyClass = 'undefined';
  } else if (typeof err === 'string') {
    details._likelyClass = 'string';
    details.message = err;
  } else {
    details.message = 'Error was neither object nor string';
  }

  return details;
}

function isNonNullObject(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
}

// A bit of duck typing
function looksLikeOAuthError(err: Error) {
  if ('error' in err) {
    if ('error_description' in err) return true;
    if (typeof err.error === 'string' && /.*access.*/i.test(err.error)) return true;
  }
  return false;
}
